import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { HomeIcon } from '@heroicons/react/24/outline';
import { FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { propertyTypes } from '../../../components/property/property-card/PropertyCardTypes';
import { detectDiscriminatoryContent } from '../../../utility/property-utilities/detectDiscriminatoryContent';
import { ListingFormData, ListingIntent } from '../types';

interface BasicInformationProps {
  register: UseFormRegister<ListingFormData>;
  errors: FieldErrors<ListingFormData>;
  watch: UseFormWatch<ListingFormData>;
  listingIntent: ListingIntent;
  setListingIntent: (intent: ListingIntent) => void;
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  register,
  errors,
  watch,
  listingIntent,
  setListingIntent
}) => {
  const title = watch('title');
  const detectedIssues = useMemo(() => {
    return detectDiscriminatoryContent(title || '');
  }, [title]);

  return (
    <motion.div
      key="singleStep1"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      {/* Progress indicator */}
      <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4 rounded-r-lg mb-6">
        <p className="text-sm text-indigo-800">
          <strong>Step 1 of 6:</strong> Let's start with the basics •{' '}
          <strong>~2 minutes</strong> remaining
        </p>
      </div>

      <h2 className="text-2xl font-semibold mb-6 flex items-center">
        <HomeIcon className="w-6 h-6 mr-2 text-blue-500" />
        Basic Information
      </h2>
      <div className="space-y-6">
        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Property Title <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Create an eye-catching title that highlights your property's best
            feature
          </p>
          <input
            {...register('title', {
              required: 'Title is required',
              maxLength: {
                value: 100,
                message: 'Title cannot exceed 100 characters'
              },
              pattern: {
                value: /^[A-Za-z0-9\s-]+$/,
                message: 'Title must be alphanumeric'
              }
            })}
            data-testid="property-title-input"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., Modern 2-Bed Apartment in Dublin City Centre"
          />
          <div className="flex justify-between mt-1">
            <div>
              {errors.title && (
                <span className="text-red-500 text-sm">
                  {errors.title.message}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400">
              {title?.length || 0}/100
            </span>
          </div>
          {detectedIssues.length > 0 && (
            <div className="bg-red-100 p-4 rounded text-red-600 mb-4 mt-4">
              <p className="font-bold">Warning:</p>
              <p>
                This title may contain discriminatory content against our
                guidelines:
              </p>
              <ul className="list-disc list-inside mt-2">
                {detectedIssues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Property Type <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Select the category that best describes your property
          </p>
          <select
            {...register('type', {
              required: 'Property type is required'
            })}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
              errors.type ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select a property type</option>
            {propertyTypes
              .filter((item) => item !== 'Any')
              .map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
          </select>
          {errors.type && (
            <span className="text-red-500 text-sm">{errors.type.message}</span>
          )}
        </div>

        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Listing Type <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-3">
            Are you offering this property for rent or sale?
          </p>
          <div className="flex flex-col sm:flex-row sm:space-x-6">
            <label className="inline-flex items-center mb-2 sm:mb-0">
              <input
                type="radio"
                value="rent"
                checked={listingIntent === 'rent'}
                onChange={() => setListingIntent('rent')}
                className="hidden"
              />
              <span
                className={`
          px-6 py-3 rounded-lg text-base font-semibold
          border-2 cursor-pointer transition-all duration-200 ease-in-out
          ${
            listingIntent === 'rent'
              ? 'bg-blue-500 text-white border-blue-500 shadow-md'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-blue-300'
          }
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
        `}
              >
                🏠 For Rent
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="sale"
                checked={listingIntent === 'sale'}
                onChange={() => setListingIntent('sale')}
                className="hidden"
              />
              <span
                className={`
          px-6 py-3 rounded-lg text-base font-semibold
          border-2 cursor-pointer transition-all duration-200 ease-in-out
          ${
            listingIntent === 'sale'
              ? 'bg-blue-500 text-white border-blue-500 shadow-md'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-blue-300'
          }
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
        `}
              >
                💰 For Sale By Owner
              </span>
            </label>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BasicInformation;
